import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

import OpenAICompatibleImg from "../Assets/openaiCompImg.png";
import observabilityImg from "../Assets/observability.jpg";
import oneClickDeployImg from "../Assets/oneClickDepl.png";
import networksecurityImg from "../Assets/networkandsecurityImg.png";

const tabs = [
  { id: "unified-observability", label: "Observability" },
  { id: "networking-security", label: "Networking and Security" },
  { id: "oneClick-ml-resource-management", label: "One-click LLM Resource Management" },
  { id: "unified-access-gateway", label: "Unified Access Gateway" },
];

const tabContent = {
  "unified-observability": {
    title: "Unified Observability & Monitoring",
    points: [
      "Monitor tokens consumed and LLM performance.",
      "Track and Visualize your experiment's data.",
      "Monitor your hyperparameters.",

    ],
    image: observabilityImg,
  },
  "networking-security": {
    title: "Networking and Security",
    points: [
      "VNET/ VPC based resource isolations.",
      "Automatic network and security configurations.",
      "Secure your models & AI assets in your existing cloud environments.",
    ],
    image: networksecurityImg,
  },
  "oneClick-ml-resource-management": {
    title: "One-click LLM Resource Management",
    points: [
      "Deploy any model/ compute/cluster/ Assets in a unified manner.",
      "One click model training and fine tuning.",
      "One click inference endpoint creations.",
    ],
    image: oneClickDeployImg,
  },
  "unified-access-gateway": {
    title: "Unified Access Gateway",
    points: [
      "Unified APIs to access any cloud providers.",
      "Unified OpenAI-compatible inferencing for LLMs.",
      "Unified portal to access all the open source and closed source models.",
    ],
    image: OpenAICompatibleImg,
  },
};

export default function FeaturesSection() {
  const [activeTab, setActiveTab] = useState("unified-observability");
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div id="features" className="bg-white text-gray-900 py-16 px-10 md:px-6  w-full max-w-screen-xl mx-auto">
     
      <p className="text-2xl md:text-3xl font-bold text-center mb-4">
        Features
      </p>
      <p className="text-center text-lg md:text-xl mb-12 text-gray-600  mx-auto">
        Efficient<span className="text-yellow-600">AI</span> Simplifies LLMops
        and AI Workflows across all cloud providers
      </p>

      <div className="hidden md:flex justify-center space-x-2 md:space-x-4 lg:space-x-10 border-b border-gray-300 pb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`relative text-sm md:text-md lg:text-lg px-2  py-2 transition-all hover:text-yellow-500 ${
              activeTab === tab.id ? "text-yellow-600" : "text-gray-900"
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            <p> {tab.label} </p>
            {activeTab === tab.id && (
              <motion.div
                layoutId="underline"
                className="absolute left-0 right-0 bottom-0 h-1 bg-yellow-600"
              />
            )}
          </button>
        ))}
      </div>

      <div
        className="md:hidden flex justify-center mb-6 relative"
        ref={dropdownRef}
      >
        <button
          className="w-full flex justify-between items-center border-b-2 border-yellow-600 bg-white px-4 py-3 rounded-lg shadow-sm text-gray-900"
          onClick={() => setDropdownOpen(!isDropdownOpen)}
        >
          <p className="text-lg font-medium">
            {tabs.find((tab) => tab.id === activeTab)?.label ||
              "Select an option"}
          </p>
          <svg
            className={`w-5 h-5 transform transition-transform duration-200 ${
              isDropdownOpen ? "rotate-180" : "rotate-0"
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        <AnimatePresence>
          {isDropdownOpen && (
            <motion.ul
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="absolute top-full left-0 mt-2 w-full bg-white rounded-lg shadow-lg z-10"
            >
              {tabs.map((tab) => (
                <li key={tab.id}>
                  <button
                    className={`w-full text-left px-4 py-3 text-gray-900  border-b border-gray-200 ${
                      activeTab === tab.id
                        ? "font-semibold text-yellow-600"
                        : ""
                    }`}
                    onClick={() => {
                      setActiveTab(tab.id);
                      setDropdownOpen(false);
                    }}
                  >
                    <p>{tab.label}</p>
                  </button>
                </li>
              ))}
            </motion.ul>
          )}
        </AnimatePresence>
      </div>

      {/* Content Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-12 items-center">

        <motion.div
          key={activeTab}
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
          className="p-8 bg-gray-100 border border-yellow-600 rounded-xl h-full shadow-lg md:text-left text-center"
        >
          <p className="text-3xl font-semibold text-gray-900 mb-4">
            {tabContent[activeTab].title}
          </p>
          <ul className="list-disc md:list-inside space-y-3 text-gray-700 text-lg">
            {tabContent[activeTab].points.map((point, index) => (
              <li key={index}><p className="inline">{point}</p></li>
            ))}
          </ul>
        </motion.div>

        <div className="flex justify-center">
          <motion.img
            src={tabContent[activeTab].image}
            alt="Feature Illustration"
            className="h-[400px] rounded-xl object-contain shadow-md cursor-pointer hover:scale-105 transition-transform duration-300"
          />
        </div>
      </div>
    </div>
  );
}
